import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, AlertTriangle } from 'lucide-react';
import { SECTIONS } from '../lib/sectionTheme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const P = SECTIONS.sops;
const today = () => new Date().toISOString().slice(0, 10);
const cls = 'w-full text-sm bg-background border border-border rounded-lg px-3 py-2';
const EMPTY = { product_type: 'contrast', product_name: '', batch_number: '', expiry_date: '', opened_date: today(),
  patient_name: '', patient_dob: '', justification: '', location: 'CT Room', notes: '' };

function F({ label, children }) {
  return <label className="block"><span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span><div className="mt-1">{children}</div></label>;
}

// Multi-dose vial register — only for vials with no single-dose alternative (CEC IPAC §5.16.1).
export function VialsTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = async () => setRows((await axios.get(`${API}/ipc/vials`, { withCredentials: true })).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault(); setErr('');
    try {
      await axios.post(`${API}/ipc/vials`, form, { withCredentials: true });
      setOpen(false); setForm({ ...EMPTY, opened_date: today() }); load();
    } catch (ex) { setErr(ex?.response?.data?.detail || 'Save failed'); }
  };
  const discard = async (id) => {
    const reason = window.prompt('Discard reason?', 'End of dedicated patient procedure');
    if (!reason) return;
    await axios.post(`${API}/ipc/vials/${id}/discard`, { reason }, { withCredentials: true });
    load();
  };

  return (
    <div data-testid="ipc-vials-tab">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">Multi-dose vial register</h2>
          <p className="text-xs text-muted-foreground max-w-2xl">
            Single-dose vials are the rule — one vial, one patient. Register a multi-dose vial only when no single-dose product
            exists on the Australian market. It is dedicated to one patient and discarded at the end of their procedure.
          </p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="vial-new-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white rounded-lg" style={{ backgroundColor: P.color }}>
          <Plus className="w-4 h-4" /> Register vial
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="bg-card border rounded-xl p-5 mb-6 space-y-3" style={{ borderColor: P.ring }} data-testid="vial-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <F label="Type">
              <select value={form.product_type} onChange={set('product_type')} className={cls} data-testid="vial-type">
                <option value="contrast">Contrast</option><option value="local_anaesthetic">Local anaesthetic</option>
                <option value="steroid">Steroid</option><option value="other">Other</option>
              </select>
            </F>
            <F label="Product"><input required value={form.product_name} onChange={set('product_name')} className={cls} data-testid="vial-product" /></F>
            <F label="Batch / lot"><input required value={form.batch_number} onChange={set('batch_number')} className={cls} data-testid="vial-batch" /></F>
            <F label="Manufacturer expiry"><input type="date" required value={form.expiry_date} onChange={set('expiry_date')} className={cls} data-testid="vial-expiry" /></F>
            <F label="Date opened"><input type="date" required value={form.opened_date} onChange={set('opened_date')} className={cls} data-testid="vial-opened" /></F>
            <F label="Location">
              <select value={form.location} onChange={set('location')} className={cls} data-testid="vial-location">
                {['CT Room', 'US Room 1', 'US Room 2', 'Procedure Suite', 'Clean Utility'].map(r => <option key={r}>{r}</option>)}
              </select>
            </F>
            <F label="Dedicated to patient *"><input required value={form.patient_name} onChange={set('patient_name')} className={cls} data-testid="vial-patient" /></F>
            <F label="Patient DOB"><input value={form.patient_dob} onChange={set('patient_dob')} placeholder="DD/MM/YYYY" className={cls} data-testid="vial-dob" /></F>
          </div>
          <F label="Why no single-dose alternative? *">
            <textarea required rows={2} value={form.justification} onChange={set('justification')} className={cls} data-testid="vial-justification" />
          </F>
          {err && <p className="text-xs text-red-600" data-testid="vial-error">{err}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
            <button type="submit" data-testid="vial-submit" className="text-sm font-semibold px-4 py-1.5 rounded-lg text-white" style={{ backgroundColor: P.color }}>Register vial</button>
          </div>
        </form>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center" data-testid="vials-empty">No multi-dose vials registered — good: single-dose only.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="px-3 py-2">Opened</th><th className="px-3 py-2">Product</th><th className="px-3 py-2">Batch</th>
              <th className="px-3 py-2">Patient</th><th className="px-3 py-2">Reason</th><th className="px-3 py-2">Status</th><th className="px-3 py-2" />
            </tr></thead>
            <tbody>
              {rows.map(v => (
                <tr key={v.vial_id} className="border-b border-border/60" data-testid={`vial-row-${v.vial_id}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{v.opened_date}</td>
                  <td className="px-3 py-2">{v.product_name}<span className="block text-[10px] text-muted-foreground">{v.product_type.replace('_', ' ')} · {v.location}</span></td>
                  <td className="px-3 py-2 font-mono text-xs">{v.batch_number}</td>
                  <td className="px-3 py-2">{v.patient_name}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground max-w-[220px]">{v.justification}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${v.status !== 'active' ? 'bg-slate-100 text-slate-500' : v.is_overdue ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>
                      {v.status !== 'active' ? 'discarded' : v.is_overdue ? 'not discarded' : 'in use today'}
                    </span>
                    {v.is_overdue && <AlertTriangle className="inline w-3 h-3 ml-1 text-red-600" />}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {v.status === 'active' && (
                      <button onClick={() => discard(v.vial_id)} data-testid={`vial-discard-${v.vial_id}`}
                        className="text-[11px] font-semibold px-2 py-1 rounded-md border border-border hover:bg-accent">Discard</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
